import { BodySecondary, H2 } from '@liveauctioneers/hammer-ui-core/text';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { CalendarBlank, CaretRight, Gavel } from '@liveauctioneers/hammer-ui-core/icons';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import {
    getCatalogSellerName,
    getCatalogStartTime,
    getCatalogTitle,
    getShouldShowLive,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCatalogUrl } from '@/utils/urls';
import { getLiveCatalogStatus } from '@/redux/modules/console';
import { getMonthDayYearTime } from '@liveauctioneers/hammer-ui-core/utils/dateTimeFormat';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { useAppSelector } from '@/redux/hooks';
import { useCallback, useMemo } from 'react';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useTrackAndNavigateCatalogCardClick } from '@/components/FeaturedCatalogCarousel/components/AutomatedCatalogCarouselCard/hooks/useTrackAndNavigateCatalogCardClick';
import AutomatedCatalogCarouselCardProgressBar from '@/components/FeaturedCatalogCarousel/components/AutomatedCatalogCarouselCard/components/ProgressBar/AutomatedCatalogCarouselCardProgressBar';
import ms from 'ms';

type Props = {
    catalogId: number;
    className?: string;
};

const AutomatedCatalogCarouselCardCatalogInfo = ({ catalogId, className = '' }: Props) => {
    const isMobile = useIsMobileLayout();
    const { formatMessage } = useIntl();

    const liveStatus = useAppSelector((state) => getLiveCatalogStatus(state, catalogId));
    const shouldShowLive = useAppSelector((state) => getShouldShowLive(state, catalogId));
    const isLive = shouldShowLive || liveStatus === 'live';

    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const houseName = useAppSelector((state) => getCatalogSellerName(state, catalogId));
    const startTime = useAppSelector((state) => getCatalogStartTime(state, catalogId));
    const catalogUrl = useMemo(() => getCatalogUrl(catalogId, title), [catalogId, title]);

    const trackCardClick = useTrackAndNavigateCatalogCardClick(catalogId);
    const onClickExplore = useCallback(() => {
        trackCardClick();
    }, [trackCardClick]);

    const datetime = useMemo(
        () =>
            new Date(
                // convert from seconds to milliseconds
                startTime * ms('1s')
            ).toISOString(),
        [startTime]
    );

    return (
        <div className={className}>
            <Link
                className="text-inherit no-underline outline-0 hover:no-underline"
                href={catalogUrl}
                onClick={(event) => {
                    event.stopPropagation();
                    onClickExplore();
                }}
            >
                <H2 className="ws-nowrap line-clamp-1 w-full overflow-hidden overflow-ellipsis text-text-inverse">
                    {title}
                </H2>
            </Link>

            <div className="mt-sm flex justify-between">
                <div
                    className={cn({
                        'w-[75%]': !isMobile,
                        'w-full': isMobile,
                    })}
                >
                    <div className="flex items-center gap-xs">
                        <Gavel
                            color="inverse"
                            size="xs"
                        />
                        <BodySecondary color="inverse">{houseName}</BodySecondary>
                    </div>
                    {isLive ? (
                        <div className="mt-xs">
                            <AutomatedCatalogCarouselCardProgressBar catalogId={catalogId} />
                        </div>
                    ) : (
                        <div className="mt-xs flex items-center gap-xs">
                            <CalendarBlank
                                color="inverse"
                                size="xs"
                            />
                            <time
                                className="hui-text-body-secondary text-text-inverse"
                                dateTime={datetime}
                            >
                                {getMonthDayYearTime(startTime)}
                            </time>
                        </div>
                    )}
                </div>
                {!isMobile && (
                    <div>
                        <Button
                            className="mt-sm"
                            iconRight={CaretRight}
                            onClick={(event) => {
                                event.stopPropagation();
                                onClickExplore();
                            }}
                            size="sm"
                            style="inverse"
                            variant="outlined"
                        >
                            {formatMessage({
                                id: 'featuredAuctions.explore',
                            })}
                        </Button>
                    </div>
                )}
            </div>
            {isMobile && (
                <Button
                    className="mt-sm w-full"
                    onClick={(event) => {
                        event.stopPropagation();
                        onClickExplore();
                    }}
                    size="sm"
                    style="inverse"
                >
                    {formatMessage({
                        id: 'featuredAuctions.exploreAuction',
                    })}
                </Button>
            )}
        </div>
    );
};

export default AutomatedCatalogCarouselCardCatalogInfo;
